import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Photo, ApiResponse, PaginatedResponse } from '../types';

interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryCondition?: (error: AxiosError) => boolean;
}

class ApiClient {
  private client: AxiosInstance;
  private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  private retryConfig: RetryConfig = {
    maxRetries: 3,
    retryDelay: 1000,
    retryCondition: (error: AxiosError) => {
      // Retry on network errors, timeouts, and 5xx server errors
      return !error.response || 
             error.code === 'ECONNABORTED' || 
             (error.response.status >= 500 && error.response.status < 600);
    }
  };

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for authentication and logging
    this.client.interceptors.request.use(
      async (config) => {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        
        // Add API key for additional security layer
        const apiKey = import.meta.env.VITE_API_KEY;
        if (apiKey) {
          config.headers['X-API-Key'] = apiKey;
        }
        
        // Add authentication token to requests
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.warn('Failed to get auth token:', error);
          // Continue with request without token - let the server handle auth errors
        }
        
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Response Error:', error);
        
        // Handle specific error codes
        if (error.response?.status === 401) {
          throw new Error('Authentication required. Please sign in again.');
        }
        if (error.response?.status === 403) {
          throw new Error('Access denied. You do not have permission to access this resource.');
        }
        if (error.response?.status === 404) {
          throw new Error('Resource not found');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests. Please wait a moment and try again.');
        }
        if (error.response?.status === 503) {
          throw new Error('Service temporarily unavailable. Please try again later.');
        }
        if (error.response?.status === 504) {
          throw new Error('Request timeout. Try using smaller page sizes or date filters to improve performance.');
        }
        if (error.response?.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        if (error.code === 'ECONNABORTED') {
          throw new Error('Request timeout. Please check your connection or try reducing the amount of data requested.');
        }
        
        // Handle custom error messages from server
        const serverMessage = error.response?.data?.message;
        if (serverMessage) {
          // Enhance specific server messages
          if (serverMessage.includes('PAGE_TOO_HIGH')) {
            throw new Error('Page number too high. Please use smaller page numbers or date filters to narrow your search.');
          }
          if (serverMessage.includes('RATE_LIMIT_EXCEEDED')) {
            throw new Error('Too many requests. Please wait a moment before trying again.');
          }
          if (serverMessage.includes('GATEWAY_TIMEOUT')) {
            throw new Error('Request took too long. Try using smaller page sizes or date filters to improve performance.');
          }
          throw new Error(serverMessage);
        }
        
        throw new Error('An unexpected error occurred');
      }
    );
  }

  // Cache management methods
  private getCacheKey(url: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${url}${paramString}`;
  }

  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() > cached.timestamp + cached.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  private setCache<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  private clearCacheByPattern(pattern: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  // Retry logic implementation
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    retries: number = this.retryConfig.maxRetries
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error) {
      if (retries > 0 && this.retryConfig.retryCondition?.(error as AxiosError)) {
        console.log(`Retrying request. Attempts remaining: ${retries}`);
        await this.delay(this.retryConfig.retryDelay);
        return this.retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async getPhotos(params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedResponse<Photo>> {
    // Validate parameters to prevent performance issues
    const validatedParams = this.validatePhotosParams(params);
    const cacheKey = this.getCacheKey('/photos', validatedParams);
    
    // Check cache first
    const cached = this.getFromCache<PaginatedResponse<Photo>>(cacheKey);
    if (cached) {
      console.log('Returning cached photos data');
      return cached;
    }

    // Make request with retry logic and extended timeout for large datasets
    const response = await this.retryRequest(async () => {
      const res: AxiosResponse<ApiResponse<PaginatedResponse<Photo>>> = await this.client.get('/photos', {
        params: validatedParams,
        timeout: 30000, // Extended timeout for large datasets
      });
      return res;
    });

    const data = response.data.data;
    
    // Validate response data
    if (!data || !Array.isArray(data.items)) {
      throw new Error('Invalid response format from server');
    }

    // Check for performance warnings
    if (data.items.length > 30) {
      console.warn(`Large dataset returned: ${data.items.length} items. Consider using smaller page sizes for better performance.`);
    }
    
    // Cache the response (shorter TTL for paginated data)
    this.setCache(cacheKey, data, 2 * 60 * 1000); // 2 minutes
    
    return data;
  }

  private validatePhotosParams(params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }) {
    if (!params) return params;

    const validated = { ...params };

    // Validate and limit page size for performance
    if (validated.limit && validated.limit > 50) {
      console.warn(`Page size ${validated.limit} is large, reducing to 50 for better performance`);
      validated.limit = 50;
    }

    // Validate page number
    if (validated.page && validated.page > 1000) {
      console.warn(`Page number ${validated.page} is very high, this may cause performance issues`);
    }

    // Validate date range
    if (validated.startDate && validated.endDate) {
      const start = new Date(validated.startDate);
      const end = new Date(validated.endDate);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        throw new Error('Invalid date format. Please use ISO date strings.');
      }
      
      if (start > end) {
        throw new Error('Start date must be before or equal to end date');
      }

      // Warn about very large date ranges
      const daysDiff = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
      if (daysDiff > 365) {
        console.warn(`Large date range selected: ${Math.round(daysDiff)} days. This may cause performance issues.`);
      }
    }

    return validated;
  }

  async getPhoto(id: string): Promise<Photo> {
    const cacheKey = this.getCacheKey(`/photos/${id}`);
    
    // Check cache first
    const cached = this.getFromCache<Photo>(cacheKey);
    if (cached) {
      console.log(`Returning cached photo data for ID: ${id}`);
      return cached;
    }

    // Make request with retry logic
    const response = await this.retryRequest(async () => {
      const res: AxiosResponse<ApiResponse<Photo>> = await this.client.get(`/photos/${id}`);
      return res;
    });

    const data = response.data.data;
    
    // Cache individual photo data (longer TTL)
    this.setCache(cacheKey, data, 10 * 60 * 1000); // 10 minutes
    
    return data;
  }

  async syncPhotos(): Promise<{ message: string; processed: number }> {
    // Clear photos cache when syncing as new photos might be available
    this.clearCacheByPattern('/photos');
    
    // Sync doesn't use cache as it's an action that changes state
    const response = await this.retryRequest(async () => {
      const res: AxiosResponse<ApiResponse<{ message: string; processed: number }>> = 
        await this.client.post('/sync');
      return res;
    });

    return response.data.data;
  }

  // Cache management utilities
  clearCache(): void {
    this.cache.clear();
    console.log('API cache cleared');
  }

  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  // Generic POST method for other endpoints
  async post<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.retryRequest(async () => {
      return await this.client.post<T>(url, data);
    });
  }

  // Update retry configuration
  updateRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config };
  }


}

export const apiClient = new ApiClient();
export default apiClient;